import { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EastIcon from "@mui/icons-material/East";
import SideNavLayout from "../Sidenav/SideNavLayout";
import api from "../api/api";

const PAGE_SIZE = 50;

const ACTION_META = {
  LOGIN: { label: "Login", icon: <LoginIcon fontSize="inherit" />, chip: "bg-green-100 text-green-700", dot: "bg-green-500" },
  LOGOUT: { label: "Logout", icon: <LogoutIcon fontSize="inherit" />, chip: "bg-gray-200 text-gray-700", dot: "bg-gray-400" },
  PAGE_VIEW: { label: "Page View", icon: <VisibilityIcon fontSize="inherit" />, chip: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  CLICK: { label: "Click", icon: <TouchAppIcon fontSize="inherit" />, chip: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
  CREATE: { label: "Create", icon: <AddCircleIcon fontSize="inherit" />, chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  EDIT: { label: "Edit", icon: <EditIcon fontSize="inherit" />, chip: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  DELETE: { label: "Delete", icon: <DeleteIcon fontSize="inherit" />, chip: "bg-red-100 text-red-700", dot: "bg-red-500" },
};

const AVATAR_COLORS = [
  "bg-[#145388]", "bg-emerald-600", "bg-violet-600", "bg-rose-600",
  "bg-amber-600", "bg-cyan-700", "bg-indigo-600", "bg-teal-600",
];

const avatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

// Human sentence for one event.
const describe = (log) => {
  const isAudit = !!log.details?.audit;
  const entity = log.details?.entity_type || "";
  switch (log.action) {
    case "LOGIN":
      return <>logged in{log.target ? <> via <b>{log.target}</b></> : null}</>;
    case "LOGOUT":
      return <>logged out</>;
    case "PAGE_VIEW":
      return <>opened the <b>{log.page || "-"}</b> screen</>;
    case "CREATE":
      return isAudit
        ? <>created {entity.toLowerCase() || "record"} <b>{log.target}</b></>
        : <>clicked <b>“{log.target}”</b> on <b>{log.page}</b></>;
    case "EDIT":
      return isAudit
        ? <>edited {entity.toLowerCase() || "record"} <b>{log.target}</b></>
        : <>clicked <b>“{log.target}”</b> on <b>{log.page}</b></>;
    case "DELETE":
      return isAudit
        ? <>deleted {entity.toLowerCase() || "record"} <b>{log.target}</b></>
        : <>clicked <b>“{log.target}”</b> on <b>{log.page}</b></>;
    default:
      return <>clicked <b>“{log.target || "-"}”</b> on <b>{log.page || "-"}</b></>;
  }
};

const dayLabel = (value) => {
  const d = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const same = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return "Today";
  if (same(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const timeLabel = (value) => {
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const StatTile = ({ label, value, accent }) => (
  <div className="flex-1 min-w-[140px] bg-white rounded-xl border shadow-sm p-4">
    <div className={`text-2xl font-bold ${accent}`}>{value ?? 0}</div>
    <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{label}</div>
  </div>
);

const AuditLog = () => {
  const [loader, setLoader] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState({});
  const [expanded, setExpanded] = useState({});

  const [searchText, setSearchText] = useState("");
  const [action, setAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [changesOnly, setChangesOnly] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await api.get("/v1/activity/logs/summary", { params });
      if (response?.status === 200) setSummary(response);
    } catch (e) {
      console.error("Failed to fetch audit summary:", e);
    }
  }, [fromDate, toDate]);

  const fetchData = useCallback(
    async (pageToLoad, append = false) => {
      try {
        setLoader(true);
        const params = { page: pageToLoad, page_size: PAGE_SIZE };
        if (searchText.trim()) params.search = searchText.trim();
        if (action) params.action = action;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;

        const response = await api.get("/v1/activity/logs", { params });
        if (response?.status === 200) {
          setLogs((prev) => (append ? [...prev, ...(response.data || [])] : response.data || []));
          setTotal(response.total || 0);
        } else if (!append) {
          setLogs([]);
          setTotal(0);
        }
      } catch (error) {
        console.error("Failed to fetch audit logs:", error);
        if (!append) {
          setLogs([]);
          setTotal(0);
        }
      } finally {
        setLoader(false);
      }
    },
    [searchText, action, fromDate, toDate]
  );

  useEffect(() => {
    setPage(1);
    const timer = setTimeout(() => {
      fetchData(1, false);
      fetchSummary();
    }, searchText ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchData, fetchSummary, searchText]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchData(next, true);
  };

  const refresh = () => {
    setPage(1);
    fetchData(1, false);
    fetchSummary();
    setExpanded({});
  };

  const visibleLogs = changesOnly ? logs.filter((l) => l.details?.audit) : logs;

  // Group by calendar day, preserving (newest-first) order.
  const groups = [];
  visibleLogs.forEach((log) => {
    const label = dayLabel(log.created_on);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(log);
    else groups.push({ label, items: [log] });
  });

  const counts = summary.counts || {};

  return (
    <SideNavLayout>
      {loader && logs.length === 0 && (
        <div className="fixed inset-0 bg-white/70 flex justify-center items-center z-[9999]">
          <CircularProgress size={60} />
        </div>
      )}

      <style>
        {`
          .audit-scroll::-webkit-scrollbar { width: 6px; }
          .audit-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 3px; }
          .audit-scroll::-webkit-scrollbar-thumb:hover { background: #9CA3AF; }
        `}
      </style>
      <div className="audit-scroll h-[88vh] overflow-y-auto overscroll-contain">
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="rounded-2xl p-6 mb-5 text-white shadow-lg bg-gradient-to-r from-[#1d2951] via-[#145388] to-[#1E6BA8] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Audit Log</h1>
            <p className="text-sm text-white/80 mt-1">
              Who did what, when — every screen visit, edit and delete, with old → new values.
            </p>
          </div>
          <button
            onClick={refresh}
            className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-4 py-2 rounded-lg text-sm font-medium backdrop-blur"
          >
            <RefreshIcon style={{ fontSize: 18 }} /> Refresh
          </button>
        </div>

        {/* Stat tiles */}
        <div className="flex flex-wrap gap-3 mb-5">
          <StatTile label="Events today" value={summary.today} accent="text-[#145388]" />
          <StatTile label="Active users today" value={summary.active_users_today} accent="text-cyan-700" />
          <StatTile label="Creates" value={counts.CREATE} accent="text-emerald-600" />
          <StatTile label="Edits" value={counts.EDIT} accent="text-amber-600" />
          <StatTile label="Deletes" value={counts.DELETE} accent="text-red-600" />
          <StatTile label="Logins" value={counts.LOGIN} accent="text-green-600" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border shadow-sm p-3 mb-5 flex flex-wrap gap-3 items-center">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm"
          >
            <option value="">All actions</option>
            {Object.entries(ACTION_META).map(([value, meta]) => (
              <option key={value} value={value}>{meta.label}</option>
            ))}
          </select>

          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm" title="From date" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm" title="To date" />

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none px-2">
            <input
              type="checkbox"
              checked={changesOnly}
              onChange={(e) => setChangesOnly(e.target.checked)}
              className="accent-[#145388]"
            />
            Data changes only
          </label>

          <input
            type="text"
            placeholder="Search user, screen or record…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="border border-gray-300 px-4 py-2 rounded-lg text-sm w-72 ml-auto"
          />
        </div>

        {/* Timeline */}
        {groups.length === 0 ? (
          <div className="bg-white rounded-xl border shadow-sm p-12 text-center text-gray-500">
            No activity found for these filters.
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.label} className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  {group.label}
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div className="space-y-2">
                {group.items.map((log) => {
                  const meta = ACTION_META[log.action] || ACTION_META.CLICK;
                  const changes = log.details?.changes || [];
                  const note = log.details?.note;
                  const isOpen = !!expanded[log.id];
                  const expandable = changes.length > 0 || !!note;

                  return (
                    <div key={log.id} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                      <div
                        className={`flex items-center gap-3 px-4 py-3 ${expandable ? "cursor-pointer hover:bg-gray-50" : ""}`}
                        onClick={() =>
                          expandable && setExpanded((prev) => ({ ...prev, [log.id]: !prev[log.id] }))
                        }
                      >
                        {/* Avatar */}
                        <div className={`w-9 h-9 min-w-9 rounded-full flex items-center justify-center text-white text-xs font-bold ${avatarColor(log.user_name)}`}>
                          {initials(log.user_name)}
                        </div>

                        {/* Sentence */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-800 truncate text-start">
                            <b>{log.user_name || "Unknown"}</b>{" "}
                            <span className="text-gray-600">{describe(log)}</span>
                          </div>
                          <div className="text-[11px] text-gray-400 mt-0.5 text-start">
                            {log.role || "-"} · {log.page || "-"}
                            {changes.length > 0 && (
                              <span className="text-[#145388] font-medium">
                                {" "}· {changes.length} field{changes.length === 1 ? "" : "s"} changed
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action chip + time */}
                        <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${meta.chip}`}>
                          <span className="text-[13px] leading-none flex">{meta.icon}</span>
                          {meta.label}
                        </span>
                        <span className="text-xs text-gray-500 whitespace-nowrap w-16 text-right">
                          {timeLabel(log.created_on)}
                        </span>
                        {expandable && (
                          <ExpandMoreIcon
                            className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                            fontSize="small"
                          />
                        )}
                      </div>

                      {/* Change detail */}
                      {isOpen && (
                        <div className="border-t bg-gray-50/70 px-4 py-3">
                          {note && (
                            <div className="text-xs text-gray-600 italic mb-2 text-start">{note}</div>
                          )}
                          {changes.length > 0 && (
                            <div className="space-y-1.5">
                              {changes.map((change, i) => (
                                <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
                                  <span className="w-28 min-w-28 font-semibold text-gray-700 capitalize text-start">
                                    {String(change.field).replace(/_/g, " ")}
                                  </span>
                                  <span className="px-2 py-1 rounded-md bg-red-50 text-red-700 line-through decoration-red-300 max-w-[260px] truncate" title={change.old}>
                                    {change.old}
                                  </span>
                                  <EastIcon style={{ fontSize: 14 }} className="text-gray-400" />
                                  <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-medium max-w-[260px] truncate" title={change.new}>
                                    {change.new}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* Load more */}
        {logs.length < total && (
          <div className="text-center mt-2 mb-8">
            <button
              onClick={loadMore}
              disabled={loader}
              className="border border-gray-300 bg-white px-5 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 shadow-sm"
            >
              {loader ? "Loading…" : `Load older events (${total - logs.length} more)`}
            </button>
          </div>
        )}
      </div>
      </div>
    </SideNavLayout>
  );
};

export default AuditLog;
